import React, { useRef, useEffect, useState, useCallback } from 'react';

const SIGNS = ['aries','taurus','gemini','cancer','leo','virgo','libra','scorpio','sagittarius','capricorn','aquarius','pisces'];
const SIGN_GLYPHS = { aries:'♈', taurus:'♉', gemini:'♊', cancer:'♋', leo:'♌', virgo:'♍', libra:'♎', scorpio:'♏', sagittarius:'♐', capricorn:'♑', aquarius:'♒', pisces:'♓' };
const SIGN_COLORS = { aries:'#E74C3C', taurus:'#27AE60', gemini:'#F39C12', cancer:'#3498DB', leo:'#E67E22', virgo:'#1ABC9C', libra:'#9B59B6', scorpio:'#C0392B', sagittarius:'#8E44AD', capricorn:'#2C3E50', aquarius:'#2980B9', pisces:'#16A085' };

const PLANET_META = {
  'Sol':{ g:'☉', c:'#FFD700' }, 'Luna':{ g:'☽', c:'#C0C0C0' }, 'Mercurio':{ g:'☿', c:'#A0A0A0' },
  'Venus':{ g:'♀', c:'#FF69B4' }, 'Marte':{ g:'♂', c:'#FF4500' }, 'Júpiter':{ g:'♃', c:'#4169E1' },
  'Saturno':{ g:'♄', c:'#8B8000' }, 'Urano':{ g:'♅', c:'#00CED1' }, 'Neptuno':{ g:'♆', c:'#4682B4' },
  'Plutón':{ g:'♇', c:'#8B0000' }, 'Quirón':{ g:'⚷', c:'#9370DB' }, 'Lilith':{ g:'⚸', c:'#4B0082' },
  'Nodo Norte':{ g:'☊', c:'#2E8B57' },
};

const ASPECT_COLORS = {
  conjunction:'#D4A017', opposition:'#880088', trine:'#0000EE', square:'#CC0000',
  sextile:'#0000EE', quincunx:'#009900', 'semi-sextile':'#009900',
};

export default function SynastryWheel({ chartDataA, chartDataB, crossAspects = [], nameA = 'A', nameB = 'B' }) {
  const CX = 350, CY = 350;
  const R_OUTER = 310, R_SIGN_MID = 285, R_SIGN_IN = 260;
  const R_PLANET_A = 230, R_PLANET_B = 165, R_ASPECT = 140;

  const [hoveredPlanet, setHoveredPlanet] = useState(null);
  const [aspectFilter, setAspectFilter] = useState('all');
  const svgRef = useRef(null);

  const ascDeg = chartDataA?.ascendant_degree || 0;

  const toSVG = (deg, r) => {
    const angle = (-(deg - ascDeg) + 180) * Math.PI / 180;
    return { x: CX + r * Math.cos(angle), y: CY + r * Math.sin(angle) };
  };

  const planetsA = chartDataA?.planets || [];
  const planetsB = chartDataB?.planets || [];
  const housesA = chartDataA?.houses || [];

  // Separate close planets
  const spreadPlanets = (planets, radius) => {
    const sorted = [...planets].sort((a,b) => a.degree - b.degree);
    const MIN_GAP = 10;
    const result = sorted.map(p => ({ ...p, displayDeg: p.degree }));
    for (let pass = 0; pass < 3; pass++) {
      for (let i = 0; i < result.length; i++) {
        for (let j = i + 1; j < result.length; j++) {
          let diff = Math.abs(result[i].displayDeg - result[j].displayDeg);
          if (diff > 180) diff = 360 - diff;
          if (diff < MIN_GAP) {
            result[i].displayDeg = (result[i].displayDeg - 2 + 360) % 360;
            result[j].displayDeg = (result[j].displayDeg + 2) % 360;
          }
        }
      }
    }
    return result;
  };

  const spreadA = spreadPlanets(planetsA, R_PLANET_A);
  const spreadB = spreadPlanets(planetsB, R_PLANET_B);

  const filteredAspects = crossAspects.filter(a => {
    if (aspectFilter === 'all') return a.draw_on_wheel;
    if (aspectFilter === 'harmony') return ['trine','sextile','conjunction'].includes(a.aspect) && a.draw_on_wheel;
    if (aspectFilter === 'tension') return ['square','opposition'].includes(a.aspect) && a.draw_on_wheel;
    return a.draw_on_wheel;
  });

  const highlightedAspects = hoveredPlanet
    ? filteredAspects.filter(a => a.planet1 === hoveredPlanet || a.planet2 === hoveredPlanet)
    : filteredAspects;

  return (
    <div data-testid="synastry-wheel">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 justify-center mb-4">
        {[['all','Todos'],['harmony','Armónicos'],['tension','Tensión']].map(([k,l]) => (
          <button key={k} onClick={() => setAspectFilter(k)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${aspectFilter === k ? 'bg-purple-500 text-white' : 'bg-white/5 text-[var(--text-muted)] hover:bg-white/10'}`}
            data-testid={`syn-filter-${k}`}>{l}</button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex gap-4 justify-center mb-3 text-xs">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" /> {nameA} (ext.)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-cyan-400 inline-block" /> {nameB} (int.)</span>
      </div>

      <svg ref={svgRef} viewBox="0 0 700 700" className="w-full max-w-[600px] mx-auto" style={{ filter: 'drop-shadow(0 0 20px rgba(139,92,246,0.15))' }}>
        <defs>
          <radialGradient id="synBg"><stop offset="0%" stopColor="#1a1040" /><stop offset="100%" stopColor="#0b0e1a" /></radialGradient>
        </defs>
        <circle cx={CX} cy={CY} r={R_OUTER} fill="url(#synBg)" stroke="rgba(230,195,106,0.15)" strokeWidth="1" />
        <circle cx={CX} cy={CY} r={R_SIGN_IN} fill="none" stroke="rgba(230,195,106,0.1)" strokeWidth="0.5" />
        <circle cx={CX} cy={CY} r={R_ASPECT} fill="none" stroke="rgba(230,195,106,0.06)" strokeWidth="0.5" />

        {/* Sign segments */}
        {SIGNS.map((sign, i) => {
          const startDeg = i * 30;
          const midDeg = startDeg + 15;
          const p1 = toSVG(startDeg, R_SIGN_IN);
          const p2 = toSVG(startDeg, R_OUTER);
          const mid = toSVG(midDeg, R_SIGN_MID);
          return (
            <g key={sign}>
              <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="rgba(230,195,106,0.12)" strokeWidth="0.5" />
              <text x={mid.x} y={mid.y} textAnchor="middle" dominantBaseline="central" fontSize="18" fill={SIGN_COLORS[sign]} style={{ pointerEvents: 'none' }}>{SIGN_GLYPHS[sign]}</text>
            </g>
          );
        })}

        {/* House cusps from Person A */}
        {housesA.map((h, i) => {
          const p1 = toSVG(h.abs_position, R_ASPECT - 10);
          const p2 = toSVG(h.abs_position, R_SIGN_IN);
          return <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="rgba(230,195,106,0.08)" strokeWidth="0.5" strokeDasharray="4,4" />;
        })}

        {/* Cross-aspect lines */}
        {highlightedAspects.map((a, i) => {
          const pA = spreadA.find(p => p.name === a.planet1);
          const pB = spreadB.find(p => p.name === a.planet2);
          if (!pA || !pB) return null;
          const posA = toSVG(pA.degree, R_ASPECT);
          const posB = toSVG(pB.degree, R_ASPECT);
          const color = ASPECT_COLORS[a.aspect] || '#888';
          const isHovered = hoveredPlanet && (a.planet1 === hoveredPlanet || a.planet2 === hoveredPlanet);
          return (
            <line key={i} x1={posA.x} y1={posA.y} x2={posB.x} y2={posB.y}
              stroke={color} strokeWidth={isHovered ? 2 : 1} opacity={isHovered ? 0.9 : hoveredPlanet ? 0.15 : 0.45}
              strokeDasharray={['opposition','square'].includes(a.aspect) ? '6,3' : 'none'}>
              <title>{a.planet1} ({nameA}) {a.aspect} {a.planet2} ({nameB}) — orbe: {a.orbit}°</title>
            </line>
          );
        })}

        {/* Person A planets (outer ring - amber) */}
        {spreadA.map((p, i) => {
          const pos = toSVG(p.displayDeg, R_PLANET_A);
          const meta = PLANET_META[p.name] || { g: '?', c: '#888' };
          const isHovered = hoveredPlanet === p.name;
          return (
            <g key={`a-${i}`}
              onMouseEnter={() => setHoveredPlanet(p.name)}
              onMouseLeave={() => setHoveredPlanet(null)}
              style={{ cursor: 'pointer' }}>
              <circle cx={pos.x} cy={pos.y} r={isHovered ? 16 : 13} fill="rgba(245,158,11,0.15)" stroke="rgba(245,158,11,0.5)" strokeWidth={isHovered ? 1.5 : 0.8} />
              <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central" fontSize={isHovered ? 16 : 14} fill={meta.c} fontWeight="bold" style={{ pointerEvents: 'none' }}>{meta.g}</text>
              <title>{p.name} ({nameA}): {p.sign || ''} {p.degree?.toFixed(1)}°</title>
            </g>
          );
        })}

        {/* Person B planets (inner ring - cyan) */}
        {spreadB.map((p, i) => {
          const pos = toSVG(p.displayDeg, R_PLANET_B);
          const meta = PLANET_META[p.name] || { g: '?', c: '#888' };
          const isHovered = hoveredPlanet === p.name;
          return (
            <g key={`b-${i}`}
              onMouseEnter={() => setHoveredPlanet(p.name)}
              onMouseLeave={() => setHoveredPlanet(null)}
              style={{ cursor: 'pointer' }}>
              <circle cx={pos.x} cy={pos.y} r={isHovered ? 16 : 13} fill="rgba(34,211,238,0.15)" stroke="rgba(34,211,238,0.5)" strokeWidth={isHovered ? 1.5 : 0.8} />
              <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central" fontSize={isHovered ? 16 : 14} fill={meta.c} fontWeight="bold" style={{ pointerEvents: 'none' }}>{meta.g}</text>
              <title>{p.name} ({nameB}): {p.sign || ''} {p.degree?.toFixed(1)}°</title>
            </g>
          );
        })}

        {/* Center labels */}
        <text x={CX} y={CY - 12} textAnchor="middle" fontSize="11" fill="rgba(245,158,11,0.7)" fontWeight="bold">{nameA}</text>
        <text x={CX} y={CY + 4} textAnchor="middle" fontSize="9" fill="rgba(230,195,106,0.4)">♡</text>
        <text x={CX} y={CY + 18} textAnchor="middle" fontSize="11" fill="rgba(34,211,238,0.7)" fontWeight="bold">{nameB}</text>
      </svg>

      {/* Aspect legend */}
      <div className="flex flex-wrap gap-3 justify-center mt-3 text-[10px] text-[var(--text-muted)]">
        {[['Conjunción','#D4A017','—'],['Trígono','#0000EE','—'],['Sextil','#0000EE','—'],['Cuadratura','#CC0000','- -'],['Oposición','#880088','- -']].map(([n,c,d]) => (
          <span key={n} className="flex items-center gap-1">
            <span style={{ display:'inline-block', width:16, height:2, background:c, borderTop: d==='- -' ? `2px dashed ${c}` : `2px solid ${c}` }} />
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}
